// Line Execute — Universal tour fallback.
//
// Every adapter surface can now offer a "Show me how instead" chip. When
// the user picks it, we call the same generateAndRegisterTour Firebase
// callable used by the legacy tour path, but with the ORIGINAL user prompt
// (not the confirm reply) so the tour speaks to what the user actually
// asked to do — not to their confirmation reply.
//
// This module owns:
//   1. The "show me how" reply regex + the per-turn quickControl the panel
//      appends to every NEEDS_CONFIRMATION and FAILED outcome.
//   2. Detection of stored "tourFallbackPrompt" on the previous assistant
//      turn — the panel persists the original prompt on the turn so the
//      resume path knows what to feed the tour generator.
//
// The dispatch itself (calling generateAndRegisterTour + emitting a tour
// card turn) lives in the panel, because tours are already coupled to the
// panel-side render path (tourDefinition, purchased, preambleKind).

// Reply that means "don't do it, walk me through it instead."
export const SHOW_ME_HOW_REGEX = /^(show\s+me\s+how|walk\s+me\s+through|guide\s+me|tour|show\s+the\s+way|how\s+do\s+I|teach\s+me|let\s+me\s+do\s+it)\b/i

export const isShowMeHowReply = (text: string): boolean => {
  const trimmed = text.trim()
  if (!trimmed) return false
  return SHOW_ME_HOW_REGEX.test(trimmed)
}

// The chip the panel appends to every adapter turn that could benefit
// from a tour alternative. Stable label/value so history persistence and
// analytics stay consistent.
export const SHOW_ME_HOW_CONTROL = {
  type: 'radio' as const,
  group: 'line-execute-adapter',
  label: 'Show me how',
  value: 'Show me how instead.',
}

// A subset of adapter kinds that a tour cannot usefully cover. Navigation
// is a single-click action — a tour just says "click here" which is worse
// than the AI just clicking; report previews are read-only so there is
// nothing to walk. Both skip the chip.
const TOUR_UNSUITABLE_KINDS = new Set(['NAVIGATION', 'REPORT_PREVIEW'])

export const adapterKindSupportsTourFallback = (kind: string): boolean =>
  !TOUR_UNSUITABLE_KINDS.has(kind)
